<template>
  <div class="new-order-list">
    <!-- PAGE HEADER -->
    <div class="ol-page-header">
      <div>
        <div class="ol-title">
          Orders
        </div>
        <div class="ol-subtitle">
          <template v-if="isFetchingOrderCount">
            All time · กำลังโหลด…
          </template>
          <template v-else>
            All time · {{ allCount }} orders
          </template>
        </div>
      </div>
      <div class="ol-branch">
        <v-icon
          size="16"
          class="mr-1">
          mdi-map-marker-outline
        </v-icon>
        <WarehouseSelect
          v-model="branchSelected"
          :items="userBranch"
          item-text="name"
          item-value="id"
          :menu-props="{ offsetY: true }"
          label="Branch"
          full-width
          dense
          solo
          flat
          hide-details
          :disabled="isFetchingOrderList" />
      </div>
    </div>

    <!-- STATUS STRIP -->
    <div class="ol-status-strip">
      <div
        v-for="status in statusStrip"
        :key="status.key"
        class="ol-status-chip"
        :class="statusChipClass(status)"
        @click="selectStatus(status.key)">
        <span
          v-if="status.key !== 'all'"
          class="ol-status-dot"
          :style="{ background: status.color }" />
        <span class="ol-status-label">{{ status.label }}</span>
        <span class="ol-status-count">
          <v-progress-circular
            v-if="isFetchingOrderCount"
            indeterminate
            :size="14"
            :width="2"
            :color="statusChipClass(status) === 'active' ? '#16160f' : '#9a9a93'" />
          <template v-else>{{ formatCount(status.count) }}</template>
        </span>
      </div>
    </div>

    <!-- TABLE CARD -->
    <div class="ol-card">
      <!-- TOOLBAR -->
      <div class="ol-toolbar">
        <v-select
          v-model="channelSelected"
          :items="channels"
          :menu-props="{ offsetY: true }"
          :disabled="isFetchingOrderList"
          item-value="value"
          item-text="label"
          class="ol-select"
          hide-details
          dense
          outlined
          auto-select-first
          @change="changeSelect()" />
        <v-select
          v-model="brandSelected"
          :items="allBrand"
          :menu-props="{ offsetY: true }"
          :disabled="isFetchingOrderList"
          item-value="value"
          item-text="text"
          class="ol-select"
          placeholder="Select Brand"
          hide-details
          dense
          outlined
          auto-select-first
          @change="changeSelect()" />

        <div class="ol-spacer"></div>

        <!-- NORMAL SEARCH INPUT -->
        <div
          v-if="isNormal"
          class="ol-search">
          <v-icon
            size="16"
            color="#a8a8a4">
            mdi-magnify
          </v-icon>
          <input
            v-model.trim="inputSearch"
            class="ol-search-input"
            placeholder="Search by Order ID"
            @keydown.enter="searchOrder()" />
          <div
            class="ol-search-go"
            @click="searchOrder()">
            <v-icon
              size="15"
              color="white">
              mdi-chevron-right
            </v-icon>
          </div>
        </div>

        <!-- SEARCH MODE TOGGLE -->
        <div class="ol-mode-toggle">
          <div
            class="ol-mode-btn"
            :class="{ active: isNormal }"
            @click="setNormalSearch()">
            ปกติ
          </div>
          <div
            class="ol-mode-btn"
            :class="{ active: isAdvanced }"
            @click="setAdvancedSearch()">
            ขั้นสูง
          </div>
        </div>
      </div>

      <!-- ADVANCED SEARCH PANEL -->
      <div
        v-if="isAdvanced"
        class="ol-advanced">
        <div class="ol-advanced-grid">
          <div class="ol-field">
            <span class="ol-field-label">Order ID</span>
            <input
              v-model.trim="advancedForm.orderId"
              placeholder="e.g. GW2026031400164-1"
              @keydown.enter="applyAdvancedSearch()" />
          </div>
          <div class="ol-field">
            <span class="ol-field-label">Phone</span>
            <input
              v-model.trim="advancedForm.phone"
              placeholder="e.g. +66987654321"
              @keydown.enter="applyAdvancedSearch()" />
          </div>
          <div class="ol-field">
            <span class="ol-field-label">Shipping ref code</span>
            <input
              v-model.trim="advancedForm.shippingRefCode"
              placeholder="e.g. TH0123456789"
              @keydown.enter="applyAdvancedSearch()" />
          </div>
          <div class="ol-field ol-field-tags">
            <span class="ol-field-label">Product</span>
            <v-autocomplete
              v-model="advancedForm.product"
              :items="productOptions"
              :search-input.sync="productSearch"
              :loading="isFetchingProducts"
              :menu-props="{ offsetY: true }"
              item-text="text"
              item-value="value"
              class="ol-tags-select"
              placeholder="ค้นหาสินค้า"
              no-filter
              clearable
              hide-details
              hide-no-data
              dense
              outlined />
          </div>
          <div class="ol-field ol-field-tags">
            <span class="ol-field-label">Product tags</span>
            <v-autocomplete
              v-model="advancedForm.tags"
              :items="productTags"
              :menu-props="{ offsetY: true }"
              :loading="isFetchingTags"
              item-text="name"
              item-value="name"
              class="ol-tags-select"
              placeholder="เลือก tags"
              multiple
              chips
              small-chips
              deletable-chips
              hide-details
              dense
              outlined />
          </div>
          <div class="ol-field">
            <span class="ol-field-label">Date from</span>
            <input
              v-model="advancedForm.dateFrom"
              type="date" />
          </div>
          <div class="ol-field">
            <span class="ol-field-label">Date to</span>
            <input
              v-model="advancedForm.dateTo"
              type="date" />
          </div>
          <div
            v-if="isCelebChannel"
            class="ol-field">
            <span class="ol-field-label">Address / Receiver Search</span>
            <input
              v-model.trim="advancedForm.shippingAddressSearch"
              placeholder="e.g. สมชาย ใจดี"
              @keydown.enter="applyAdvancedSearch()" />
          </div>
        </div>
        <div class="ol-advanced-actions">
          <div
            class="ol-btn-ghost"
            @click="clearAdvancedSearch()">
            ล้างค่า
          </div>
          <div
            class="ol-btn-dark"
            @click="applyAdvancedSearch()">
            <v-icon
              size="15"
              color="white">
              mdi-magnify
            </v-icon>
            ค้นหา
          </div>
        </div>
      </div>

      <!-- BULK ACTIONS -->
      <div class="ol-bulk">
        <span class="ol-bulk-count">{{ orderSelected.length }} selected</span>
        <template v-if="orderStatus === 'paid'">
          <button
            class="ol-bulk-btn"
            :disabled="orderSelected.length === 0 || !isMainOrder"
            @click="rtsPress()">
            RTS ({{ orderSelected.length }})
          </button>
          <button
            class="ol-bulk-btn"
            :disabled="orderSelected.length === 0"
            @click="exportOrders()">
            EXPORT ({{ orderSelected.length }})
          </button>
          <button
            class="ol-bulk-btn"
            :disabled="orderSelected.length === 0"
            @click="exportItems()">
            EXPORT ITEM ({{ orderSelected.length }})
          </button>
        </template>
        <template v-else-if="orderStatus === 'ready_to_ship'">
          <button
            class="ol-bulk-btn"
            :disabled="orderSelected.length === 0 || isMainOrder || !isDhl"
            @click="dhlProcess()">
            ส่ง DHL ({{ orderSelected.length }})
          </button>
          <button
            class="ol-bulk-btn"
            :disabled="orderSelected.length === 0 || isMainOrder || !isDhlExpress || !isNonShipment"
            @click="confirmDhlExpress()">
            ส่ง DHL EXPRESS ({{ orderSelected.length }})
          </button>
          <button
            class="ol-bulk-btn"
            :disabled="orderSelected.length === 0 || isMainOrder"
            @click="printClick()">
            PRINT LABEL ({{ orderSelected.length }})
          </button>
          <button
            class="ol-bulk-btn"
            :disabled="orderSelected.length === 0"
            @click="exportOrders()">
            EXPORT ({{ orderSelected.length }})
          </button>
          <button
            class="ol-bulk-btn"
            :disabled="orderSelected.length === 0"
            @click="exportItems()">
            EXPORT ITEM ({{ orderSelected.length }})
          </button>
        </template>
        <template v-else-if="orderStatus === 'completed'">
          <button
            class="ol-bulk-btn"
            :disabled="orderSelected.length === 0 || isMainOrder || !isDhl"
            @click="dhlProcess()">
            ส่ง DHL ({{ orderSelected.length }})
          </button>
          <button
            class="ol-bulk-btn"
            :disabled="orderSelected.length === 0 || isMainOrder || !isDhlExpress || !isNonShipment"
            @click="confirmDhlExpress()">
            ส่ง DHL EXPRESS ({{ orderSelected.length }})
          </button>
          <button
            class="ol-bulk-btn"
            :disabled="orderSelected.length === 0 || !isMainOrder"
            @click="printOrderSummary()">
            PRINT ORDER SUMMARY ({{ orderSelected.length }})
          </button>
          <button
            class="ol-bulk-btn"
            :disabled="orderSelected.length === 0"
            @click="exportOrders()">
            EXPORT ({{ orderSelected.length }})
          </button>
          <button
            class="ol-bulk-btn"
            :disabled="orderSelected.length === 0"
            @click="exportItems()">
            EXPORT ITEM ({{ orderSelected.length }})
          </button>
        </template>
        <template v-else>
          <button
            class="ol-bulk-btn"
            :disabled="orderSelected.length === 0"
            @click="exportOrders()">
            EXPORT ({{ orderSelected.length }})
          </button>
          <button
            class="ol-bulk-btn"
            :disabled="orderSelected.length === 0"
            @click="exportItems()">
            EXPORT ITEM ({{ orderSelected.length }})
          </button>
        </template>

        <!-- HIDE PRE-ORDER SWITCH -->
        <div
          class="ol-hide-preorder"
          :class="{ active: hidePreOrder }"
          :title="hidePreOrder ? 'กำลังซ่อน Pre-Order — กดอีกครั้งเพื่อแสดงทั้งหมด' : 'ซ่อนออเดอร์ที่เป็น Pre-Order'"
          @click="toggleHidePreOrder()">
          <v-icon
            size="17"
            :color="hidePreOrder ? '#c1167c' : '#85857c'">
            mdi-eye-off-outline
          </v-icon>
          <span class="ol-hide-label">ซ่อน Pre-Order</span>
          <span
            class="ol-switch"
            :class="{ on: hidePreOrder }">
            <span class="ol-switch-knob" />
          </span>
        </div>
      </div>

      <!-- PAGINATION (TOP) -->
      <div class="ol-pagination ol-pagination-top">
        <div class="ol-pagination-size">
          <span>Rows per page:</span>
          <select
            :value="perPage"
            :disabled="isFetchingOrderList"
            @change="changePerPage($event)">
            <option
              v-for="size in perPageOptions"
              :key="size"
              :value="size">
              {{ size }}
            </option>
          </select>
        </div>
        <span class="ol-pagination-range">{{ rangeInfo }}</span>
        <div class="ol-pagination-nav">
          <div
            class="ol-nav-btn"
            :class="{ disabled: !canPrev }"
            title="Previous"
            @click="prevPage()">
            <v-icon size="22">
              mdi-chevron-left
            </v-icon>
          </div>
          <div
            class="ol-nav-btn"
            :class="{ disabled: !canNext }"
            title="Next"
            @click="nextPage()">
            <v-icon size="22">
              mdi-chevron-right
            </v-icon>
          </div>
        </div>
      </div>

      <!-- TABLE -->
      <div class="ol-table">
        <div class="ol-table-scroll">
          <div class="ol-table-inner">
            <!-- HEADER -->
            <div class="ol-row ol-row-head">
              <div class="ol-cell-check">
                <div
                  class="ol-checkbox"
                  :class="{ checked: allSelected }"
                  @click="toggleSelectAll()">
                  <v-icon
                    v-if="allSelected"
                    size="14"
                    color="white">
                    mdi-check
                  </v-icon>
                </div>
              </div>
              <div class="ol-th">
                Order ID
              </div>
              <div class="ol-th">
                Products
              </div>
              <div class="ol-th ol-center">
                Receiver / Note
              </div>
              <div class="ol-th ol-right">
                Net Price
              </div>
              <div class="ol-th ol-center">
                Channel
              </div>
              <div class="ol-th ol-center">
                Create Time
              </div>
              <div class="ol-th">
                Shipment
              </div>
            </div>

            <!-- LOADING -->
            <div
              v-if="isFetchingOrderList"
              class="ol-empty">
              <v-progress-circular
                indeterminate
                color="#16160f"
                size="28" />
              <span>Loading orders…</span>
            </div>

            <!-- EMPTY -->
            <div
              v-else-if="orderList.length === 0"
              class="ol-empty">
              ไม่พบออเดอร์
            </div>

            <!-- ROWS -->
            <div
              v-for="item in orderList"
              v-else
              :key="item.orderId"
              class="ol-row ol-row-body"
              :class="{ selected: isSelected(item) }">
              <div class="ol-cell-check">
                <div
                  class="ol-checkbox"
                  :class="{ checked: isSelected(item) }"
                  @click="toggleSelect(item)">
                  <v-icon
                    v-if="isSelected(item)"
                    size="14"
                    color="white">
                    mdi-check
                  </v-icon>
                </div>
              </div>

              <!-- ORDER ID -->
              <div class="ol-cell-id">
                <div class="ol-id-line">
                  <span
                    class="ol-status-dot"
                    :style="{ background: statusColor(item.currentState) }" />
                  <span
                    class="ol-id-text"
                    @click="openOrderDetail(item.orderId)">
                    {{ item.orderId }}
                  </span>
                  <v-tooltip top>
                    <template #activator="{ on, attrs }">
                      <v-icon
                        v-if="isModify(item)"
                        v-bind="attrs"
                        size="16"
                        color="orange"
                        v-on="on">
                        mdi-pencil-circle-outline
                      </v-icon>
                    </template>
                    <span>{{ $t(`info.modifined order`) }}</span>
                  </v-tooltip>
                </div>
                <span
                  v-if="isPreOrder(item)"
                  class="ol-preorder-badge">
                  Pre-order
                </span>
                <span
                  v-if="item.lineMyShopOrderId"
                  class="ol-sub-id ol-myshop">
                  {{ item.lineMyShopOrderId }}
                </span>
                <span
                  v-if="item.tiktokShopOrderId"
                  class="ol-sub-id ol-tiktok">
                  TikTok : {{ item.tiktokShopOrderId }}
                </span>
                <span
                  v-if="item.lazadaShopOrderId"
                  class="ol-sub-id ol-lazada">
                  Lazada : {{ item.lazadaShopOrderId }}
                </span>
              </div>

              <!-- PRODUCTS -->
              <div class="ol-cell-products">
                <div
                  v-for="(sku, index) in visibleSkus(item)"
                  :key="`${item.orderId}-${index}`"
                  class="ol-product-line">
                  <span class="ol-product-dot">·</span>
                  <span>({{ sku.amount }}) - {{ sku.name }}</span>
                </div>
                <div
                  v-if="hiddenSkuCount(item) > 0"
                  class="ol-product-more">
                  +{{ hiddenSkuCount(item) }} more
                </div>
              </div>

              <!-- RECEIVER / NOTE -->
              <div class="ol-cell-receiver">
                <div class="ol-receiver-name">
                  {{ getReceiverFromShippingOrder(item.shippingAddress) }}
                </div>
                <div
                  v-if="item.note"
                  class="ol-receiver-note">
                  NOTE : {{ item.note }}
                </div>
              </div>

              <!-- NET PRICE -->
              <div class="ol-cell-price">
                {{ calcNetPrice(item) | showNumberFormat() }}
              </div>

              <!-- CHANNEL -->
              <div class="ol-cell-channel">
                <span class="ol-channel-chip">{{ item.channel }}</span>
              </div>

              <!-- CREATE TIME -->
              <div class="ol-cell-time">
                <div>{{ $dayjs(item.createdAt).format('DD/MM/YYYY') }}</div>
                <div>{{ $dayjs(item.createdAt).format('HH:mm') }}</div>
              </div>

              <!-- SHIPMENT -->
              <div class="ol-cell-shipment">
                <v-btn
                  v-if="!isMainOrder"
                  class="ol-print-btn"
                  icon
                  x-small
                  title="Print"
                  @click="print(item.orderId)">
                  <v-icon size="16">
                    mdi-printer
                  </v-icon>
                </v-btn>
                <span class="ol-shipment-code">{{ item.shipping.refCode || '-' }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- PAGINATION -->
        <div class="ol-pagination">
          <div class="ol-pagination-size">
            <span>Rows per page:</span>
            <select
              :value="perPage"
              :disabled="isFetchingOrderList"
              @change="changePerPage($event)">
              <option
                v-for="size in perPageOptions"
                :key="size"
                :value="size">
                {{ size }}
              </option>
            </select>
          </div>
          <span class="ol-pagination-range">{{ rangeInfo }}</span>
          <div class="ol-pagination-nav">
            <div
              class="ol-nav-btn"
              :class="{ disabled: !canPrev }"
              title="Previous"
              @click="prevPage()">
              <v-icon size="22">
                mdi-chevron-left
              </v-icon>
            </div>
            <div
              class="ol-nav-btn"
              :class="{ disabled: !canNext }"
              title="Next"
              @click="nextPage()">
              <v-icon size="22">
                mdi-chevron-right
              </v-icon>
            </div>
          </div>
        </div>
      </div>
    </div>

    <order-detail
      :order-detail-id="orderDetailId"
      :is-main-order="isMainOrder"
      @re-fetching="reFetchOrder" />
    <dhl-result
      v-if="isShowDHLResult"
      v-model="isShowDHLResult"
      :success="successData"
      :errors="errorData" />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import ExportToXLSX from 'xlsx'
import OrderChannel from '@/assets/json/OrderChannel.json'
import ShippingCountries from '@/assets/json/ShippingCountries.json'
import NotSupportWY from '@/assets/json/NotSupportWY.json'
import OrderProvider from '@/resources/OrderProvider'
import StoreProvider from '@/resources/StoreProvider'
import DHLProvider from '@/resources/DHLProvider'
import ProductProvider from '@/resources/ProductProvider'
import ProductAttributeProvider from '@/resources/ProductAttributeProvider'
import CheckOrderStatusState from '@/assets/js/CheckOrderStatusState'
import WarehouseSelect from '@/components/WarehouseSelect.vue'
import orderDetail from '../component/OrderDetail.vue'
import dhlResult from '../component/DHLResult.vue'

const orderChannel = OrderChannel.reduce((obj, item) => Object.assign(obj, { [item.value]: item.label }), {})
const ProductAttributeService = new ProductAttributeProvider()
const ProductService = new ProductProvider()
const OrderService = new OrderProvider()
const DHLService = new DHLProvider()
const StoreService = new StoreProvider()

const STATUS_ORDER = [
  'all',
  'pending',
  'reserved',
  'paid',
  'waiting',
  'ready_to_ship',
  'completed',
  'exchange_return',
  'void',
  'expired',
  'deleted'
]

const STATUS_COLORS = {
  pending: '#cddc39',
  reserved: '#ff9800',
  paid: '#9c27b0',
  waiting: '#00bcd4',
  ready_to_ship: '#e91e8c',
  completed: '#1a2744',
  exchange_return: '#4caf50',
  void: '#9e9e9e',
  expired: '#795548',
  deleted: '#f44336'
}

export default {
  components: {
    orderDetail,
    dhlResult,
    WarehouseSelect
  },
  data () {
    return {
      maxOrderSelect: 25,
      maxVisibleSkus: 5,
      inputSearch: '',
      activeSearch: '',
      advanceSearch: '0',
      advancedForm: {
        orderId: '',
        phone: '',
        shippingRefCode: '',
        shippingAddressSearch: '',
        product: '',
        tags: [],
        dateFrom: '',
        dateTo: ''
      },
      productTags: [],
      isFetchingTags: false,
      productOptions: [],
      productSearch: '',
      isFetchingProducts: false,
      productSearchTimer: null,
      appliedFilters: {
        startDate: null,
        endDate: null,
        productTags: [],
        memberTel: null,
        productId: null,
        shippingRefCode: null,
        shippingAddressSearch: null
      },
      channels: OrderChannel,
      branchSelected: 0,
      channelSelected: 'all',
      brandSelected: 'all',
      page: 1,
      perPage: 25,
      perPageOptions: [10, 25, 50, 100, 200],
      hidePreOrder: true,
      orderSelected: [],
      isShowDHLResult: false,
      successData: [],
      errorData: [],
      allBrand: [],
      orderDetailId: '',
      ready: false,
      orderList: [],
      isFetchingOrderList: false,
      isFetchingOrderCount: false,
      statusList: {
        all: { label: 'All', count: 0 },
        pending: { label: 'Pending', count: 0 },
        reserved: { label: 'Reserved', count: 0 },
        paid: { label: 'Paid', count: 0 },
        waiting: { label: 'Waiting', count: 0 },
        ready_to_ship: { label: 'RTS', count: 0 },
        completed: { label: 'Completed', count: 0 },
        exchange_return: { label: 'E/R', count: 0 },
        void: { label: 'Void', count: 0 },
        expired: { label: 'Expired', count: 0 },
        deleted: { label: 'Deleted', count: 0 }
      }
    }
  },
  computed: {
    ...mapGetters({
      userBranch: 'Store/userBranch'
    }),
    orderStatus () {
      return this.$route.params.orderStatus || 'all'
    },
    idQuery () {
      return this.$route.query.id ? this.$route.query.id : ''
    },
    isMainOrderQuery () {
      return this.$route.query.is_main ? this.$route.query.is_main === 'true' : false
    },
    search () {
      return this.$route.query?.search || ''
    },
    isMainOrder () {
      return this.branchSelected === 0
    },
    isDhl () {
      return this.orderSelected.every((o) => o.shipping.method !== 'dhl_express')
    },
    isDhlExpress () {
      return this.orderSelected.every((o) => o.shipping.method === 'dhl_express')
    },
    isNonShipment () {
      return this.orderSelected.every((o) => !o.shipping.refCode)
    },
    branchId () {
      return Number(this.$route.query?.branch) || 0
    },
    channelLabel () {
      return this.$route.query?.channel || 'all'
    },
    brandLabel () {
      return this.$route.query?.brand || 'all'
    },
    isCelebChannel () {
      return this.channelSelected === 'celeb'
    },
    isNormal () {
      return this.advanceSearch === '0'
    },
    isAdvanced () {
      return this.advanceSearch === '1'
    },
    allCount () {
      return this.formatCount(this.statusList.all?.count || 0)
    },
    statusStrip () {
      return STATUS_ORDER
        .filter((key) => this.statusList[key])
        .map((key) => ({
          key,
          label: this.statusList[key].label,
          count: this.statusList[key].count || 0,
          color: STATUS_COLORS[key] || '#bdbdbd'
        }))
    },
    allSelected () {
      return this.orderList.length > 0
        && this.orderList.every((order) => this.isSelected(order))
    },
    totalOrder () {
      return this.statusList[this.orderStatus]?.count || 0
    },
    totalPage () {
      return Math.max(1, Math.ceil(this.totalOrder / this.perPage))
    },
    canPrev () {
      return this.page > 1
    },
    canNext () {
      return this.page < this.totalPage
    },
    rangeInfo () {
      if (!this.totalOrder) {
        return '0-0 of 0'
      }
      const start = ((this.page - 1) * this.perPage) + 1
      const end = Math.min(this.page * this.perPage, this.totalOrder)
      return `${start}-${end} of ${this.formatCount(this.totalOrder)}`
    }
  },
  watch: {
    $route (newVal, oldVal) {
      if (newVal.name === oldVal.name && newVal.params.orderStatus !== oldVal.params.orderStatus) {
        this.resetData()
        this.initOrderList()
        this.fetchPage()
        this.fetchOrderCountMethod()
      }
    },
    isMainOrder (val) {
      this.setIsMainOrder(val)
    },
    hidePreOrder () {
      if (!this.ready) {
        return
      }
      this.changeSelect()
    },
    branchSelected () {
      if (!this.ready) {
        return
      }
      this.changeSelect()
    },
    productSearch (val) {
      clearTimeout(this.productSearchTimer)
      this.productSearchTimer = setTimeout(() => this.fetchProductOptions(val), 350)
    }
  },
  async mounted () {
    await this.getBrand()
    this.getTags()
    this.initOrderList()
    this.ready = true
    this.fetchPage()
    this.fetchOrderCountMethod()
    if (this.idQuery !== '') {
      this.setIsMainOrder(this.isMainOrderQuery)
      this.openOrderDetail(this.idQuery, this.isMainOrderQuery)
    }
  },
  methods: {
    ...mapActions({
      selectOrderId: 'Order/selectOrderId',
      setIsMainOrder: 'Order/setIsMainOrder',
      setLoading: 'Components/setLoading',
      setModal: 'Components/setModal',
      setSnackbar: 'Components/setSnackbar'
    }),
    formatCount (value) {
      return Number(value || 0).toLocaleString('en-US')
    },
    statusColor (state) {
      return STATUS_COLORS[state] || '#bdbdbd'
    },
    statusChipClass (status) {
      return status.key === this.orderStatus ? 'active' : ''
    },
    isPreOrder (order) {
      return Array.isArray(order.tags) && order.tags.some((tag) => String(tag).toLowerCase() === 'pre-order')
    },
    visibleSkus (order) {
      return Array.isArray(order.skus) ? order.skus.slice(0, this.maxVisibleSkus) : []
    },
    hiddenSkuCount (order) {
      const total = Array.isArray(order.skus) ? order.skus.length : 0
      return Math.max(0, total - this.maxVisibleSkus)
    },
    toggleHidePreOrder () {
      this.hidePreOrder = !this.hidePreOrder
    },
    isSelected (item) {
      return this.orderSelected.some((order) => order.orderId === item.orderId)
    },
    toggleSelect (item) {
      if (this.isSelected(item)) {
        this.orderSelected = this.orderSelected.filter((order) => order.orderId !== item.orderId)
      } else {
        this.orderSelected = [...this.orderSelected, item]
      }
    },
    toggleSelectAll () {
      if (this.allSelected) {
        const displayedIds = new Set(this.orderList.map((order) => order.orderId))
        this.orderSelected = this.orderSelected.filter((order) => !displayedIds.has(order.orderId))
      } else {
        const merged = [...this.orderSelected]
        this.orderList.forEach((order) => {
          if (!this.isSelected(order)) {
            merged.push(order)
          }
        })
        this.orderSelected = merged
      }
    },
    setNormalSearch () {
      if (this.isNormal) {
        return
      }
      this.advanceSearch = '0'
    },
    setAdvancedSearch () {
      if (this.isAdvanced) {
        return
      }
      this.advanceSearch = '1'
    },
    emptyFilters () {
      return {
        startDate: null,
        endDate: null,
        productTags: [],
        memberTel: null,
        productId: null,
        shippingRefCode: null,
        shippingAddressSearch: null
      }
    },
    buildFilterParams () {
      const filters = {}
      if (this.appliedFilters.startDate) {
        filters.startDate = this.appliedFilters.startDate
      }
      if (this.appliedFilters.endDate) {
        filters.endDate = this.appliedFilters.endDate
      }
      if (this.appliedFilters.memberTel) {
        filters.memberTel = this.appliedFilters.memberTel
      }
      if (this.appliedFilters.productId) {
        filters.productId = this.appliedFilters.productId
      }
      if (this.appliedFilters.shippingRefCode) {
        filters.shippingRefCode = this.appliedFilters.shippingRefCode
      }
      if (this.appliedFilters.shippingAddressSearch) {
        filters.shippingAddressSearch = this.appliedFilters.shippingAddressSearch
      }
      if (this.appliedFilters.productTags.length) {
        filters.productTags = this.appliedFilters.productTags
      }
      return filters
    },
    applyAdvancedSearch () {
      // Order ID maps to the free-text search; everything else maps to structured query params.
      this.activeSearch = (this.advancedForm.orderId || '').trim()
      this.appliedFilters = {
        startDate: this.advancedForm.dateFrom || null,
        endDate: this.advancedForm.dateTo || null,
        productTags: [...this.advancedForm.tags],
        memberTel: (this.advancedForm.phone || '').trim() || null,
        productId: this.advancedForm.product || null,
        shippingRefCode: (this.advancedForm.shippingRefCode || '').trim() || null,
        shippingAddressSearch: this.isCelebChannel ? (this.advancedForm.shippingAddressSearch || '').trim() || null : null
      }
      this.changeSelect()
    },
    clearAdvancedSearch () {
      this.advancedForm = {
        orderId: '',
        phone: '',
        shippingRefCode: '',
        shippingAddressSearch: '',
        product: '',
        tags: [],
        dateFrom: '',
        dateTo: ''
      }
      this.productOptions = []
      this.productSearch = ''
      this.activeSearch = ''
      this.appliedFilters = this.emptyFilters()
      this.changeSelect()
    },
    async getBrand () {
      try {
        const { data } = await ProductAttributeService.getProductAttribute('brands', {
          limit: 999
        })

        this.allBrand = [
          {
            text: 'All Brand',
            value: 'all'
          },
          ...data.results.map((brand) => ({
            text: brand.name,
            value: brand.name
          }))
        ]
      } catch (error) {
        console.error('getAttribute: ', error)
      }
    },
    async getTags () {
      try {
        this.isFetchingTags = true
        const { data } = await ProductAttributeService.getProductAttribute('tags', {
          page: 1,
          itemsPerPage: 9999999,
          sortBy: ['id'],
          sortDesc: [true]
        })
        this.productTags = data.results.map((tag) => ({ name: tag.name }))
      } catch (error) {
        console.error('getTags: ', error)
      } finally {
        this.isFetchingTags = false
      }
    },
    async fetchProductOptions (term) {
      const keyword = (term || '').trim()
      if (keyword.length < 2) {
        return
      }
      try {
        this.isFetchingProducts = true
        const { data } = await ProductService.getProducts({
          page: 1,
          limit: 20,
          search: keyword
        })
        this.productOptions = data.results.map((product) => ({
          text: product.name,
          value: product.id
        }))
      } catch (error) {
        console.error('fetchProductOptions: ', error)
      } finally {
        this.isFetchingProducts = false
      }
    },
    printOrderSummary () {
      const bills = JSON.stringify(this.orderSelected.map((or) => or.orderId))
      window.localStorage.setItem('print-order-summay', bills)
      this.$router.push({ name: 'PrintOrderSummary' })
    },
    exportOrders () {
      if (!this.orderSelected.length) {
        return
      }

      if (this.isMainOrder) {
        const exportData = this.orderSelected.map((v) => ({
          date: this.$dayjs(v.createdAt).format('YYYY-MM-DD HH:mm:ss'),
          orderId: v.orderId,
          channel: v.channel,
          currentState: v.currentState,
          itemQty: v.skus.reduce((sum, s) => sum + s.amount, 0),
          shippingFee: v.shipping.fee,
          net: v.net,
          note: v.note,
          firstName: v.shippingAddress.firstName,
          lastName: v.shippingAddress.lastName,
          email: v.shippingAddress.email,
          contactNo: v.shippingAddress.contactNo,
          address: v.shippingAddress.address,
          address2: v.shippingAddress.address2,
          subDistrict: v.shippingAddress.subDistrict,
          district: v.shippingAddress.district,
          stateProvince: v.shippingAddress.stateProvince,
          country: v.shippingAddress.country,
          postcode: v.shippingAddress.postcode
        }))

        this.exportDHLToXlsx(exportData, 'order-list')
      } else {
        const exportData = this.orderSelected.map((v) => ({
          date: this.$dayjs(v.createdAt).format('YYYY-MM-DD HH:mm:ss'),
          orderId: v.orderId,
          warehouseCode: v.warehouse.code,
          channel: v.channel,
          currentState: v.currentState,
          itemQty: v.skus.reduce((sum, s) => sum + s.amount, 0),
          shippingFee: v.shipping.fee,
          promotionCode: v.promotionCode?.code || '',
          discount: v.discount,
          net: v.net,
          note: v.note,
          firstName: v.shippingAddress.firstName,
          lastName: v.shippingAddress.lastName,
          email: v.shippingAddress.email,
          contactNo: v.shippingAddress.contactNo,
          address: v.shippingAddress.address,
          address2: v.shippingAddress.address2,
          subDistrict: v.shippingAddress.subDistrict,
          district: v.shippingAddress.district,
          stateProvince: v.shippingAddress.stateProvince,
          country: v.shippingAddress.country,
          postcode: v.shippingAddress.postcode
        }))

        this.exportDHLToXlsx(exportData, 'order-list')
      }
    },
    exportItems () {
      if (!this.orderSelected.length) {
        return
      }

      const exportData = this.orderSelected.reduce((rows, order) => {
        const skus = Array.isArray(order.skus) ? order.skus : []

        skus.forEach((sku) => {
          rows.push({
            orderId: order.orderId,
            createdAt: this.$dayjs(order.createdAt).format('YYYY-MM-DD HH:mm:ss'),
            channel: order.channel,
            warehouse: order.warehouse?.name || '',
            skuCode: sku.code,
            name: sku.name,
            color: sku.color,
            size: sku.size,
            qty: sku.amount,
            price: sku.price
          })
        })

        return rows
      }, [])

      this.exportDHLToXlsx(exportData, 'order-item-list')
    },
    calcNetPrice (item) {
      return item.net || 0
    },
    isModify (order) {
      return this.isMainOrder && order.regenFrom
    },
    async reFetchOrder () {
      this.fetchPage()
      this.fetchOrderCountMethod()
    },
    async fetchPage () {
      try {
        this.isFetchingOrderList = true
        this.orderList = []

        let currentState = null

        if (this.orderStatus !== 'deleted') {
          currentState = this.orderStatus !== 'all' ? this.orderStatus : null
        }

        let params = {
          currentState: currentState || null,
          channel: this.channelSelected.toLowerCase() || 'all',
          page: this.page || 1,
          limit: this.perPage || 25,
          warehouse: this.isMainOrder ? null : this.branchSelected,
          brand: this.brandSelected || 'all',
          search: this.activeSearch || null,
          sortBy: null,
          status: this.orderStatus === 'deleted' ? 'deleted' : 'active',
          sortOrder: 'desc'
        }

        if (this.hidePreOrder) {
          params.tags = ['pre-order']
          params.tagInclude = '0'
        }

        if (currentState === 'void' || currentState === 'exchange_return') {
          params = { ...params, sortBy: ['updatedAt'], sortOrder: 'desc' }
        } else if (this.sortOrderAsc()) {
          params = { ...params, sortBy: ['createdAt'], sortOrder: 'asc' }
        }

        params = { ...params, ...this.buildFilterParams() }

        let result = null

        if (this.isMainOrder) {
          const { data } = await OrderService.getOrderList(params)
          result = data
        } else if (params.status === 'deleted') {
          result = { limit: 25, page: 1, pages: 1, results: [], total: 1 }
        } else {
          const { data } = await OrderService.getOrderListByWarehouse(params)
          result = data
        }

        this.orderList = result.results.map((order) => ({
          ...order,
          channel: orderChannel[order.channel] || order.channel,
          currentState: CheckOrderStatusState(order)
        }))
      } catch (error) {
        console.error('fetchPage', error)
      } finally {
        this.isFetchingOrderList = false
      }
    },
    async fetchOrderCountMethod () {
      try {
        this.isFetchingOrderCount = true
        const paramsQuery = {
          channel: this.channelSelected.toLowerCase(),
          brand: this.brandSelected || 'all',
          search: this.activeSearch,
          advanceSearch: this.advanceSearch || '0',
          ...this.buildFilterParams()
        }

        if (this.hidePreOrder) {
          paramsQuery.tags = ['pre-order']
          paramsQuery.tagInclude = '0'
        }

        let orderCount = null

        if (this.branchSelected === 0) {
          const { data } = await OrderService.getOrderCount(paramsQuery)
          orderCount = data
        } else {
          const { data } = await OrderService.getOrderCountByWarehouse(this.branchSelected, paramsQuery)
          data.deleted = 0
          orderCount = data
        }

        if (orderCount) {
          for (const status of Object.keys(orderCount)) {
            let key = status
            if (status === 'readyToShip') {
              key = 'ready_to_ship'
            } else if (status === 'exchangeReturn') {
              key = 'exchange_return'
            }
            if (this.statusList[key]) {
              this.statusList[key].count = orderCount[status] || 0
            }
          }
        }
      } catch (error) {
        console.error('fetchOrderCountMethod', error)
      } finally {
        this.isFetchingOrderCount = false
      }
    },
    getReceiverFromShippingOrder (shippingAddress) {
      let text = ''
      const { firstName, lastName, note } = shippingAddress

      if (firstName) {
        text += `${firstName} `
      }

      if (lastName) {
        text += `${lastName}`
      }

      if (note) {
        text = note
      }

      return text || ''
    },
    sortOrderAsc () {
      const dateAsc = ['paid', 'ready_to_ship']
      const asc = dateAsc.some((status) => status === this.orderStatus)

      return asc
    },
    initOrderList () {
      this.branchSelected = this.branchId
      this.channelSelected = this.channelLabel
      this.brandSelected = this.brandLabel
      this.inputSearch = this.search
      this.activeSearch = this.search
      this.clearSelected()
    },
    selectStatus (status) {
      if (status === this.orderStatus) {
        return
      }
      const query = {
        branch: this.branchSelected || null,
        channel: this.channelSelected || null,
        brand: this.brandSelected || null,
        search: this.activeSearch || ''
      }
      for (const key of Object.keys(query)) {
        if (!query[key]) {
          delete query[key]
        }
      }
      this.$router.push({
        name: this.$route.name,
        params: { orderStatus: status },
        query
      }, () => {})
    },
    clearSelected () {
      this.orderSelected = []
    },
    changeSelect () {
      this.clearSelected()
      this.setRouterQuery()
      this.page = 1
      this.resetData()
      this.fetchPage()
      this.fetchOrderCountMethod()
    },
    resetData () {
      this.orderList = []
    },
    changePerPage (event) {
      this.perPage = Number(event.target.value) || 25
      this.page = 1
      this.fetchPage()
    },
    prevPage () {
      if (!this.canPrev) {
        return
      }
      this.page -= 1
      this.clearSelected()
      this.fetchPage()
    },
    nextPage () {
      if (!this.canNext) {
        return
      }
      this.page += 1
      this.clearSelected()
      this.fetchPage()
    },
    setRouterQuery () {
      const query = {
        branch: this.branchSelected || null,
        channel: this.channelSelected || null,
        brand: this.brandSelected || null,
        search: this.activeSearch || ''
      }
      for (const key of Object.keys(query)) {
        if (!query[key]) {
          delete query[key]
        }
      }
      this.$router.push({
        name: this.$route.name,
        params: { orderStatus: this.orderStatus },
        query
      }, () => {})
    },
    checkPaymentChannel (payments) {
      if (Array.isArray(payments)) {
        return payments[0]?.channel || '-'
      }
      return '-'
    },
    searchOrder () {
      this.activeSearch = this.inputSearch
      this.appliedFilters = this.emptyFilters()
      this.changeSelect()
    },
    openOrderDetail (orderId, isMain = true) {
      this.orderDetailId = orderId
      this.selectOrderId({
        id: orderId,
        isMainOrder: isMain ? this.isMainOrder : false
      })
    },
    print (orderId) {
      this.setPrintList([orderId])
      const routeData = this.$router.resolve({ name: 'OrderPrintLabel' })
      window.open(routeData.href, '_blank')
    },
    printClick () {
      this.setPrintList(this.orderSelected.map((order) => order.orderId))
      const routeData = this.$router.resolve({ name: 'OrderPrintLabel' })
      window.open(routeData.href, '_blank')
    },
    setPrintList (payload) {
      window.localStorage.removeItem('print-list')
      window.localStorage.setItem('print-list', JSON.stringify(payload))
    },
    async getDhlPickupId () {
      const { data: { dhlEcommerceSetting: { pickupWarehouses, dhlPrefix, filename } } } = await StoreService.getStoreSetting()
      return { pickupWarehouses, dhlPrefix, filename }
    },
    async dhlProcess () {
      try {
        const { pickupWarehouses, dhlPrefix, filename } = await this.getDhlPickupId()
        this.setLoading({
          active: true,
          clickAble: false,
          message: 'Creating shipment...'
        })

        const bills = JSON.parse(JSON.stringify(this.orderSelected))

        const payload = []
        const data = bills.map((row) => {
          const foundpickupId = pickupWarehouses.find((warehouse) => warehouse.id === row.warehouse.id)
          const pickupId = foundpickupId?.pickupId || null
          const orderPrefix = row.orderId.substring(0, 2)
          const orderId = row.orderId.replace('-', '')
          const cutDigit = orderId.substring(2)
          let increaseDigit = 1

          if (row.shipping.refCode) {
            const prefix = row.shipping.refCode.substring(0, 8)

            if (prefix === `${dhlPrefix}${orderPrefix}D`) {
              const postfix = row.shipping.refCode.substring(row.shipping.refCode.length - 2)
              increaseDigit = Number(postfix) + 1
            }
          }

          increaseDigit = increaseDigit.toString().padStart(2, 0)

          const trackNumber = `${orderPrefix}D${cutDigit}${increaseDigit}`

          payload.push({
            orderId: row.orderId,
            providerName: 'DHL',
            method: 'pdo',
            refCode: `${dhlPrefix}${trackNumber}`
          })

          const address = `${row.shippingAddress.address} ${row.shippingAddress.address2 || ''} ${row.shippingAddress.subDistrict}`
          const address1 = `${address.slice(0, 55)}`
          const address2 = `-${address.slice(55, 110)}`
          const address3 = `-${address.slice(110, 165)}`

          const tmpData = {
            'เลขบัญชีที่รับสินค้า': pickupId,
            'ช่องทางการขาย': '',
            'รหัสการจัดส่ง': trackNumber,
            'รหัสบริการจัดส่ง': 'PDO',
            'บริษัท': '',
            'ชื่อผู้รับ': `${row.shippingAddress.firstName} ${row.shippingAddress.lastName}`,
            'ที่อยู่บรรทัดที่ 1': address1,
            'ที่อยู่บรรทัดที่ 2': address2,
            'ที่อยู่บรรทัดที่ 3': address3,
            'เขต (อำเภอ)': row.shippingAddress.district,
            'จังหวัด': row.shippingAddress.stateProvince,
            'รหัสไปรษณีย์': row.shippingAddress.postcode,
            'รหัสประเทศปลายทาง': 'TH',
            'หมายเลขโทรศัพท์': row.shippingAddress.contactNo,
            'อีเมล์์': row.shippingAddress.email,
            'น้ำหนักสินค้า (กรัม)': '100',
            'ความยาว (ซม)': '',
            'ความกว้าง (ซม)': '',
            'ความสูง (ซม)': '',
            'สกุลเงิน': 'THB',
            'ยอดรวมมูลค่าสินค้า': '',
            'ประกัน': '',
            'มูลค่าการทำประกัน': '',
            'เก็บเงินปลายทาง': '',
            'มูลค่าการเก็บเงินปลายทาง': '',
            'รายละเอียดการจัดส่ง': '',
            'หมายเหตุ': '',
            'ชื่อบริษัทผู้จัดส่ง': '',
            'ชื่อผู้จัดส่ง': '',
            'ที่อยู่ผู้จัดส่งบรรทัดที่ 1': '',
            'ที่อยู่ผู้จัดส่งบรรทัดที่ 2': '',
            'ที่อยู่ผู้จัดส่งบรรทัดที่ 3': '',
            'เขต (อำเภอ) ผู้จัดส่ง': '',
            'จังหวัดผู้จัดส่ง': '',
            'รหัสไปรษณีย์ผู้จัดส่ง': '',
            'รหัสประเทศปลายทางผู้จัดส่ง': '',
            'หมายเลขโทรศัพท์ผู้จัดส่ง': '',
            'อีเมล์ผู้จัดส่ง': '',
            'รหัสบริการส่งคืนสินค้า': '',
            'ชื่อบริษัทที่ส่งคืน': '',
            'ชื่อที่ส่งคืน': '',
            'ที่อยู่ที่ส่งคืนบรรทัดที่ 1': '',
            'ที่อยู่ที่ส่งคืนบรรทัดที่ 2': '',
            'ที่อยู่ที่ส่งคืนบรรทัดที่ 3': '',
            'เขต (อำเภอ) ที่ส่งคืน': '',
            'จังหวัดที่ส่งคืน': '',
            'รหัสไปรษณีย์ที่ส่งคืน': '',
            'รหัสประเทศปลายทางที่ส่งคืน': '',
            'หมายเลขโทรศัพท์ที่ส่งคืน': '',
            'อีเมล์ที่ส่งคืน': '',
            'บริการ 1': '',
            'บริการ 2': '',
            'กระบวนการ Handover': '',
            'โหมดการส่งคืนของ': '',
            'การอ้างอิงการเรียกเก็บเงิน 1': '',
            'การอ้างอิงการเรียกเก็บเงิน 2': '',
            'IsMult': '',
            'ตัวเลือกการจัดส่ง': '',
            'รหัสชิ้น': '',
            'คำอธิบายรายชิ้น': '',
            'น้ำหนักรายชิ้น': '',
            'การเรียกเก็บเงินปลายทางรายชิ้น': '',
            'มูลค่าการทำประกันรายชิ้น': '',
            'การอ้างอิงการเรียกเก็บเงินรายชิ้น 1': '',
            'การอ้างอิงการเรียกเก็บเงินรายชิ้น 2': ''
          }

          return tmpData
        })

        await this.addTrackingNo(payload, data, filename)
      } catch (error) {
        console.error('dhlProcess', error)
        this.setSnackbar({
          value: true,
          message: `Error: ${error.message}`,
          type: 'error'
        })
      } finally {
        this.setLoading({ active: false })
      }
    },
    async confirmDhlExpress () {
      if (this.orderSelected.some((order) => !!order.shipping.refCode)) {
        const orderIds = this.orderSelected
          .filter((order) => !!order.shipping.refCode)
          .map((order) => order.orderId)

        this.setModal({
          value: true,
          title: 'ส่ง DHL Express',
          message: `ออเดอร์\n${orderIds.join('\n')}\nมีการส่ง DHL Express ไปแล้ว ต้องการส่งอีกครั้งหรือไม่?`,
          confirmText: 'ใช่',
          confirmType: 'error',
          cancelType: '',
          cancelText: 'ไม่ใช่',
          onConfirm: () => this.dhlExpressProcess()
        })
      } else {
        this.dhlExpressProcess()
      }
    },
    async dhlExpressProcess () {
      try {
        this.setLoading({
          active: true,
          clickAble: false,
          message: 'Creating shipment...'
        })

        for await (const order of this.orderSelected) {
          const foundCountry = ShippingCountries.find((c) => c.country.toLowerCase() === order.shippingAddress.country.toLowerCase())

          if (!foundCountry) {
            throw Error('Country or postal code was wrong.')
          }

          const { data } = await DHLService.createShipment({
            orderId: order.orderId,
            receiverCountryCode: foundCountry.code
          })

          if (NotSupportWY.includes(foundCountry.code)) {
            data.documents.forEach((document) => {
              this.downloadPDF(document.content, `${order.orderId}-${document.typeCode}`)
            })
          } else {
            const document = data.documents.find((doc) => doc.typeCode === 'label')

            if (document) {
              this.downloadPDF(document.content, `${order.orderId}-${document.typeCode}`)
            }
          }

          await OrderService.updateTrackingOrder(order.orderId, data.shipmentTrackingNumber)
        }

        this.setSnackbar({
          value: true,
          message: 'Created shipment successfully.',
          type: 'success'
        })

        this.reFetchOrder()
      } catch (error) {
        console.error('dhlExpressProcess', error)
        this.setSnackbar({
          value: true,
          message: `Error: ${error.message}`,
          type: 'error'
        })
      } finally {
        this.setLoading({ active: false })
      }
    },
    async addTrackingNo (payload, data, filename) {
      this.setLoading({
        active: true,
        clickAble: false,
        message: 'Updating order...'
      })

      const exportData = []
      this.successData = []
      this.errorData = []

      for await (const [index, item] of payload.entries()) {
        try {
          await OrderService.updateTrackingOrderDHL(item)
          this.successData.push(item.orderId)
          exportData.push(data[index])
        } catch (error) {
          this.errorData.push({
            code: error.code,
            orderId: item.orderId,
            message: error.message
          })
          console.error('addTrackingNo', error)
        }
      }

      this.setLoading({ active: false })
      this.orderSelected = []
      this.reFetchOrder()
      this.isShowDHLResult = true

      if (exportData.length) {
        this.exportDHLToXlsx(exportData, filename)
      }
    },
    exportDHLToXlsx (data, fileName) {
      const workSheet = ExportToXLSX.utils.json_to_sheet(data)
      const newWorkbook = ExportToXLSX.utils.book_new()
      ExportToXLSX.utils.book_append_sheet(newWorkbook, workSheet, 'Sheet1')
      ExportToXLSX.writeFile(newWorkbook, `${fileName}.xlsx`)
    },
    downloadPDF (pdf, fileName) {
      const linkSource = `data:application/pdf;base64,${pdf}`
      const downloadLink = document.createElement('a')

      downloadLink.href = linkSource
      downloadLink.download = `${fileName}.pdf`
      downloadLink.click()
    },
    async rtsPress () {
      this.setLoading({
        active: true,
        clickAble: false,
        message: 'CHANGE STATUS TO READY TO SHIP...'
      })
      try {
        if (this.orderSelected.length > this.maxOrderSelect) {
          this.setSnackbar({
            value: true,
            message: `Orders maximum selects at ${this.maxOrderSelect} orders`,
            type: 'error'
          })

          this.orderSelected = this.orderSelected.slice(0, this.maxOrderSelect)

          return
        }

        const { hasError } = await this.splitReadyToShip(this.orderSelected)
        if (!hasError) {
          this.setSnackbar({
            value: true,
            message: 'All orders is ready to ship',
            type: 'success'
          })
        } else {
          this.setSnackbar({
            value: true,
            message: 'Ready to ship is completed, but some order can\'t',
            type: 'error'
          })
        }
        this.orderSelected = []
        this.reFetchOrder()
      } catch (error) {
        console.error(error)
        this.setSnackbar({
          value: true,
          message: `Error: ${error.message}`,
          type: 'error'
        })
      } finally {
        this.setLoading({ active: false })
      }
    },
    async splitReadyToShip (orders) {
      let hasError = false
      const config = {
        exceed: 5
      }
      const queue = orders
      const cycles = Math.ceil(queue.length / config.exceed)

      for (let cycle = 1; cycle <= cycles; cycle++) {
        try {
          const current = queue.splice(0, config.exceed)
          const unpromised = current.map((each) => this.fetchOrderDetail(each.orderId))
          // eslint-disable-next-line no-await-in-loop
          const promised = await Promise.all(unpromised)
          // eslint-disable-next-line no-await-in-loop
          await Promise.all(promised.map((each) => this.readyToShip(each)))
        } catch {
          hasError = true
          this.setSnackbar({
            value: true,
            message: `some order is error on preparing to ship at stage ${cycle}/${cycles}`,
            type: 'error'
          })
        }
      }

      return { hasError }
    },
    readyToShip (payload) {
      const { orderId } = payload
      return OrderService.updateStatusToReadyToShip(orderId)
    },
    async fetchOrderDetail (id) {
      let fetchData = null
      try {
        const { data } = await OrderService.getOrderById(id)
        fetchData = data
      } catch (error) {
        console.error('fetchOrderDetail', error)
        this.setSnackbar({
          value: true,
          message: `Error: ${error.message}`,
          type: 'error'
        })
      }

      return fetchData
    }
  }
}
</script>

<style lang="scss" scoped>
$ol-bg: #f6f6f4;
$ol-ink: #16160f;
$ol-muted: #85857c;
$ol-muted-2: #6f6f68;
$ol-faint: #9a9a93;
$ol-border: #e6e6e2;
$ol-border-soft: #f0f0ee;
$ol-pink: #e91e8c;
$ol-pink-dark: #c1167c;

.new-order-list {
  background: $ol-bg;
  font-family: 'Archivo', 'Noto Sans Thai', sans-serif;
  color: $ol-ink;
  padding: 16px 20px 32px;
  min-height: 100%;
}

.ol-page-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 24px;
}
.ol-title {
  font-size: 30px;
  font-weight: 700;
  letter-spacing: -0.5px;
  line-height: 1.1;
}
.ol-subtitle {
  font-size: 13.5px;
  color: $ol-muted;
  margin-top: 7px;
}
.ol-branch {
  display: flex;
  align-items: center;
  background: #fff;
  border: 1px solid $ol-border;
  border-radius: 10px;
  padding: 4px 12px;
  min-width: 230px;
}
.ol-branch ::v-deep .v-input__slot {
  box-shadow: none !important;
}

/* STATUS STRIP */
.ol-status-strip {
  display: flex;
  align-items: stretch;
  gap: 8px;
  margin-bottom: 22px;
  flex-wrap: wrap;
}
.ol-status-chip {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #fff;
  border: 1px solid $ol-border;
  border-radius: 10px;
  padding: 11px 16px;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: #fafaf8;
  }
  &.active {
    background: $ol-ink;
    border-color: $ol-ink;

    .ol-status-label {
      color: #fff;
    }
    .ol-status-count {
      color: $ol-ink;
      background: #fff;
      border-radius: 20px;
      padding: 1px 9px;
    }
  }
}
.ol-status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex: 0 0 auto;
}
.ol-status-label {
  font-size: 13.5px;
  font-weight: 600;
  color: #3a3a33;
}
.ol-status-count {
  font-size: 13px;
  font-weight: 700;
  color: $ol-faint;
}
.ol-status-count .v-progress-circular {
  vertical-align: middle;
}

/* CARD */
.ol-card {
  background: #fff;
  border: 1px solid #ececea;
  border-radius: 14px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  overflow: hidden;
}

/* TOOLBAR */
.ol-toolbar {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px 22px;
  border-bottom: 1px solid $ol-border-soft;
  flex-wrap: wrap;
}
.ol-select {
  max-width: 200px;
  flex: 0 0 auto;
}
.ol-spacer {
  flex: 1;
}
.ol-mode-toggle {
  display: flex;
  align-items: center;
  border: 1px solid $ol-border;
  border-radius: 9px;
  overflow: hidden;
  height: 42px;
  flex: 0 0 auto;
}
.ol-mode-btn {
  display: flex;
  align-items: center;
  height: 100%;
  padding: 0 16px;
  font-size: 13.5px;
  font-weight: 500;
  color: $ol-muted-2;
  cursor: pointer;

  & + & {
    border-left: 1px solid $ol-border;
  }
  &:hover {
    background: #f5f5f2;
  }
  &.active {
    background: $ol-ink;
    color: #fff;
    font-weight: 600;
    border-left-color: $ol-ink;
  }
}
.ol-search {
  display: flex;
  align-items: center;
  gap: 10px;
  border: 1px solid $ol-border;
  border-radius: 9px;
  padding: 0 8px 0 14px;
  height: 42px;
  width: 360px;
  flex: 0 0 auto;
}
.ol-search-input {
  border: none;
  outline: none;
  font-size: 14px;
  font-family: inherit;
  flex: 1;
  background: transparent;
  color: $ol-ink;
}
.ol-search-go {
  width: 30px;
  height: 30px;
  background: $ol-ink;
  border-radius: 7px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

/* HIDE PRE-ORDER */
.ol-hide-preorder {
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 9px 14px;
  border-radius: 9px;
  cursor: pointer;
  user-select: none;

  &:hover {
    background: #f5f5f2;
  }
  &.active {
    background: #faf0f6;

    &:hover {
      background: #f7e6f1;
    }
    .ol-hide-label {
      color: $ol-pink-dark;
      font-weight: 600;
    }
  }
}
.ol-hide-label {
  font-size: 14px;
  font-weight: 500;
  color: $ol-muted-2;
}
.ol-switch {
  width: 40px;
  height: 23px;
  border-radius: 14px;
  background: #e0e0db;
  position: relative;
  flex: 0 0 auto;
  transition: background 0.2s ease;

  &.on {
    background: $ol-pink;
  }
}
.ol-switch-knob {
  position: absolute;
  top: 2.5px;
  left: 2.5px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  transition: left 0.2s ease;

  .ol-switch.on & {
    left: 19.5px;
  }
}

/* ADVANCED PANEL */
.ol-advanced {
  padding: 22px;
  border-bottom: 1px solid $ol-border-soft;
  background: #fbfbfa;
}
.ol-advanced-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px 20px;
}
.ol-field {
  display: flex;
  flex-direction: column;
  gap: 7px;

  > input {
    border: 1px solid $ol-border;
    border-radius: 8px;
    padding: 10px 12px;
    font-size: 14px;
    font-family: inherit;
    outline: none;
    background: #fff;
    color: $ol-ink;
  }
}
.ol-tags-select ::v-deep .v-input__slot {
  border-radius: 8px;
  background: #fff;
}
.ol-tags-select ::v-deep fieldset {
  border-color: $ol-border;
}
.ol-field-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.7px;
  text-transform: uppercase;
  color: $ol-faint;
}
.ol-advanced-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 18px;
}
.ol-btn-ghost {
  font-size: 13.5px;
  font-weight: 600;
  color: $ol-muted-2;
  border: 1px solid $ol-border;
  border-radius: 8px;
  padding: 10px 20px;
  cursor: pointer;
  background: #fff;

  &:hover {
    background: #f5f5f2;
  }
}
.ol-btn-dark {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13.5px;
  font-weight: 600;
  color: #fff;
  background: $ol-ink;
  border-radius: 8px;
  padding: 10px 22px;
  cursor: pointer;

  &:hover {
    background: #000;
  }
}

/* BULK ACTIONS */
.ol-bulk {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 13px 22px;
  border-bottom: 1px solid $ol-border-soft;
  background: #fbfbfa;
}
.ol-bulk-count {
  font-size: 13px;
  color: #a0a09a;
  margin-right: auto;
}
.ol-bulk-btn {
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.3px;
  color: $ol-ink;
  border: 1px solid $ol-ink;
  border-radius: 8px;
  padding: 8px 16px;
  background: #fff;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.15s ease;

  &:hover:not(:disabled) {
    background: $ol-ink;
    color: #fff;
  }
  &:disabled {
    color: #bcbcb6;
    border-color: #ececea;
    background: #fff;
    cursor: default;
  }
}

/* TABLE */
.ol-table {
  width: 100%;
}
.ol-table-scroll {
  width: 100%;
  overflow-x: auto;
}
.ol-table-inner {
  min-width: 1316px;
}
.ol-row {
  display: grid;
  grid-template-columns: 46px 200px minmax(280px, 1fr) 200px 96px 134px 120px 240px;
  align-items: center;
  padding: 14px 22px;
  border-bottom: 1px solid $ol-border-soft;
}
.ol-row-head {
  background: #fbfbfa;
}
.ol-row-body {
  padding: 20px 22px;
  border-bottom: 1px solid #f3f3f1;
  transition: background 0.12s ease;

  &:hover {
    background: #fafaf8;
  }
  &.selected {
    background: #faf0f6;
  }
}
.ol-th {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  color: $ol-faint;
}
.ol-center {
  text-align: center;
}
.ol-right {
  text-align: right;
}
.ol-cell-check {
  display: flex;
  justify-content: center;
}
.ol-checkbox {
  width: 18px;
  height: 18px;
  border: 1.5px solid #cfcfc8;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.12s ease, border-color 0.12s ease;

  &.checked {
    background: $ol-ink;
    border-color: $ol-ink;
  }
}
.ol-cell-id {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.ol-id-line {
  display: flex;
  align-items: center;
  gap: 8px;
}
.ol-id-text {
  color: $ol-ink;
  font-size: 13.5px;
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;
  word-break: break-all;
}
.ol-preorder-badge {
  align-self: flex-start;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.3px;
  color: #fff;
  background: #f5a623;
  border-radius: 14px;
  padding: 3px 12px;
}
.ol-sub-id {
  font-size: 11px;
}
.ol-myshop {
  color: #18a352;
}
.ol-tiktok {
  align-self: flex-start;
  padding: 2px 4px;
  border-radius: 4px;
  font-size: 10px;
  color: #fff;
  background: #000;
}
.ol-lazada {
  align-self: flex-start;
  padding: 2px 4px;
  border-radius: 4px;
  font-size: 10px;
  color: #fff;
  background: #ff38d3;
}
.ol-cell-products {
  font-size: 13.5px;
  color: #2a2a23;
  line-height: 1.55;
}
.ol-product-line {
  display: flex;
  gap: 8px;
  margin: 2px 0;
}
.ol-product-dot {
  color: #c4c4bd;
}
.ol-product-more {
  margin-top: 4px;
  font-size: 12.5px;
  font-weight: 600;
  color: $ol-muted;
}
.ol-cell-receiver {
  text-align: center;
  font-size: 13.5px;
  color: #2a2a23;
  line-height: 1.5;
}
.ol-receiver-name {
  font-weight: 500;
}
.ol-receiver-note {
  color: $ol-pink-dark;
  font-size: 12.5px;
  margin-top: 6px;
  line-height: 1.5;
}
.ol-cell-price {
  text-align: right;
  font-size: 14px;
  font-weight: 600;
  color: $ol-ink;
}
.ol-cell-channel {
  display: flex;
  justify-content: center;
}
.ol-channel-chip {
  font-size: 12px;
  font-weight: 500;
  color: #5f5f58;
  background: #f2f2ef;
  border-radius: 6px;
  padding: 4px 10px;
  white-space: nowrap;
}
.ol-cell-time {
  text-align: center;
  font-size: 13px;
  color: $ol-muted;
  line-height: 1.5;
}
.ol-cell-shipment {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12.5px;
  color: $ol-muted;
}
.ol-shipment-code {
  flex: 1 1 auto;
  min-width: 0;
  word-break: break-all;
}
.ol-print-btn {
  flex: 0 0 auto;
}
.ol-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 64px 0;
  font-size: 14px;
  color: $ol-muted;
}

/* PAGINATION */
.ol-pagination {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 32px;
  padding: 12px 22px;
  border-top: 1px solid $ol-border-soft;
  background: #fbfbfa;
}
.ol-pagination-top {
  border-top: none;
  border-bottom: 1px solid $ol-border-soft;
}
.ol-pagination-size {
  display: flex;
  align-items: center;
  gap: 14px;
  font-size: 13.5px;
  color: #5f5f58;

  select {
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    border: none;
    border-bottom: 1px solid #d4d4ce;
    background: transparent;
    font-family: inherit;
    font-size: 13.5px;
    color: $ol-ink;
    padding: 3px 22px 5px 4px;
    cursor: pointer;
    outline: none;
  }
}
.ol-pagination-range {
  font-size: 13.5px;
  color: $ol-ink;
}
.ol-pagination-nav {
  display: flex;
  align-items: center;
  gap: 14px;
}
.ol-nav-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  border-radius: 50%;
  cursor: pointer;
  color: #3a3a33;

  &:hover {
    background: #efefec;
  }
  &.disabled {
    color: #cfcfc8;
    cursor: default;
    pointer-events: none;
  }
}
</style>
